import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React from 'react';
import { Heading, Icon, Separator, Table, TableCell, TableRow, Tooltip, TooltipToggle } from 'telia-front-react';
import { useStores } from '../../stores/RootStore';
import { PrivateMobileCardProps } from './../PrivateMobileCard';
import PrivateMobileDetailsTableCell from './PrivateMobileDetailsTableCell';

const DEFAULT_CALLS_AMOUNT = 300;
const DEFAULT_SMS_AMOUNT = 100;

const PrivateMobileInclusions = observer((props: PrivateMobileCardProps) => {
  const { isMobile } = useIsMobileHook();
  const { translate } = React.useContext(TranslationsContext);
  const { mobileLifeStore } = useStores();
  const { multiUserRoamingAmount, internetAmount, roamingAmount, packageName, calls, sms } = props;
  const callsAmount = calls || DEFAULT_CALLS_AMOUNT;
  const smsAmount = sms || DEFAULT_SMS_AMOUNT;

  const getRoamingAmount = (): number => {
    return !!multiUserRoamingAmount && mobileLifeStore.selectedDeviceCount > 1 ? multiUserRoamingAmount : roamingAmount;
  };

  const renderTableRow = (detailIcon: string, detailName: string, detailValue: React.JSX.Element): React.JSX.Element => (
    <TableRow>
      {!isMobile && (
        <PrivateMobileDetailsTableCell width={1} padding="none">
          <Icon modifiers={`#${detailIcon}`} size="sm" />
        </PrivateMobileDetailsTableCell>
      )}
      <PrivateMobileDetailsTableCell width={isMobile ? 10 : 9} padding={isMobile ? 'none' : 'default'}>
        {translate(`mobile.details.inclusions.${detailName}`)}
      </PrivateMobileDetailsTableCell>
      <PrivateMobileDetailsTableCell width={2} padding="none" className="text-right">
        {detailValue}
      </PrivateMobileDetailsTableCell>
    </TableRow>
  );

  const callsTooltip = (): React.JSX.Element => {
    return (
      <Tooltip
        referenceElement={
          <TooltipToggle>
            <strong>{translate('mobile.details.inclusions.calls.tooltipToggle')}</strong>
          </TooltipToggle>
        }
      >
        {translate('mobile.details.inclusions.calls.tooltip', { calls: callsAmount, sms: smsAmount })}
      </Tooltip>
    );
  };

  return (
    <>
      <Heading level={2} className="h4 text-bold">
        {translate('mobile.details.inclusions.title')}
      </Heading>
      <Separator spacing="xxsmall" color="strong" weight="thin" />
      <Table>
        <tbody>
          <TableRow>
            {!isMobile && (
              <TableCell padding="none" valign="top" width={1}>
                <Separator color="invisible" spacing="xxsmall" />
                <Icon modifiers="#service-internet" size="sm" />
              </TableCell>
            )}
            <TableCell padding={isMobile ? 'none' : 'default'} valign="middle" width={isMobile ? 10 : 9}>
              {isMobile && <Separator color="invisible" spacing="xxsmall" />}
              {translate('mobile.details.inclusions.internet')}
              <Separator color="invisible" spacing="xxsmall" />
              {translate('mobile.details.inclusions.roaming')}
              {isMobile && <Separator color="invisible" spacing="xxsmall" />}
            </TableCell>
            <TableCell padding="none" valign="middle" width={2} className="text-right">
              {isMobile && <Separator color="invisible" spacing="xxsmall" />}
              {<strong>{internetAmount || packageName}</strong>}
              <Separator color="invisible" spacing="xxsmall" />
              {<strong>{`${getRoamingAmount()} ${translate('mobile.details.inclusions.internetUnit')}`}</strong>}
              {isMobile && <Separator color="invisible" spacing="xxsmall" />}
            </TableCell>
          </TableRow>
          {renderTableRow('phone', 'callsAndSms', callsTooltip())}
          {renderTableRow('mobile-world', 'calls', <strong>{translate('mobile.details.inclusions.callsValue', { calls: callsAmount })}</strong>)}
          {renderTableRow('sms', 'sms', <strong>{translate('mobile.details.inclusions.smsValue', { sms: smsAmount })}</strong>)}
        </tbody>
      </Table>
      <Separator color="invisible" spacing="xsmall" />
    </>
  );
});

export default PrivateMobileInclusions;
